import { Capacitor } from "@capacitor/core";

import { NativeUsageStats } from "../native/plugins";

/**
 * Android launcher-icon resolution. On Android, Screen0 usage rows carry the real
 * package name as `appIdentifier`, so icons resolve natively via PackageManager —
 * Apple Search / iTunes is never used on Android (see AppIcon.tsx). Results are cached
 * per package for the session; a single in-flight batch request per package dedupes
 * concurrent AppIcon mounts.
 */

export type AndroidIconState = string | null;

// packageName -> resolved data URI (or null when the package has no icon / is gone).
const iconCache = new Map<string, AndroidIconState>();
const inFlight = new Map<string, Promise<AndroidIconState>>();

export function isAndroidNative(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

/** True for reverse-DNS package identifiers (e.g. "com.instagram.android"). */
export function isAndroidPackageName(value: string | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed.includes(".")) return false;
  return /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/.test(trimmed);
}

export function getCachedAndroidIcon(packageName: string): AndroidIconState | undefined {
  return iconCache.get(packageName);
}

export async function resolveAndroidIcon(packageName: string): Promise<AndroidIconState> {
  if (iconCache.has(packageName)) return iconCache.get(packageName) ?? null;
  const existing = inFlight.get(packageName);
  if (existing) return existing;

  const request = (async () => {
    try {
      const { icons } = await NativeUsageStats.getAppIcons({ packageNames: [packageName] });
      const match = icons.find((entry) => entry.packageName === packageName);
      const resolved = match?.icon ?? null;
      iconCache.set(packageName, resolved);
      return resolved;
    } catch (error) {
      console.error("NativeUsageStats.getAppIcons failed", error);
      iconCache.set(packageName, null);
      return null;
    } finally {
      inFlight.delete(packageName);
    }
  })();

  inFlight.set(packageName, request);
  return request;
}
